import { RouteComponentProps } from "@gatsbyjs/reach-router";
import { APAsyncButton, APCard, APCenter, APColumn, APExpanded, APForm, APFormFieldText, APGrid, APRow, APSizedBox, APText, getIt, handleErrorMessage, showErrorDialog, useAPForm } from "ap-components";
import React, { useEffect, useState } from "react";
import { Heading, Icons, Note } from ".";
import SharedPrefs from "../../Services/SharedPrefs";
import MandateModel from "../../Models/Mandate";
import { BankAccount } from "./BankAccount";
import LoadingState from "./LoadingState";
import MandateScrollContainer from "./MandateScrollContainer";
import MutualFundApiServices from "../../Services/MutualFundServices";

const LIMITS = ["10000", "25000", "50000", "100000", "500000", "1000000"];

export default function ReviewMandateDetails(props: RouteComponentProps<{ mandateId: string }>) {
    let [mandateAmount, setMandateAmount] = useState("");
    const [mandate, setMandate] = useState<MandateModel>();
    const [upiId, setUpiId] = useState<string>();
    let control = useAPForm();
    useEffect(() => {
        getMandate()
    }, [])

    const mandateToken = getIt(SharedPrefs).getItem('mandateTkn');
    if (!mandateToken) {
        window.location.href = '/';
        showErrorDialog("Unauthorized Access");
        return <></>
    }

    if (!props.mandateId) {
        window.history.back();
        showErrorDialog("Please select correct mandate");
        return <></>
    }

    async function getMandate() {
        if (!mandateToken || !props.mandateId) {
            return
        }

        try {
            const mandateDetails = await getIt(MutualFundApiServices).getMandate(props.mandateId, mandateToken);
            setMandate(mandateDetails);
            if (mandateDetails.isAutoGenerated) {
                setMandateAmount(mandateDetails.mandateAmount);
            }
        } catch (error) {
            handleErrorMessage(error);
        }
    }

    if (!mandate) {
        return <LoadingState />
    }

    let mandateType = mandate.getMandateType();

    return (
        <APColumn mainAxisSize="max" crossAxisAlignment="center" >
            <APExpanded>
                <APForm control={control}>
                    <MandateScrollContainer
                        scrollContent={
                            <APColumn crossAxisAlignment="stretch">
                                <APCard style={{ padding: "16px", borderRadius: "8px" }}>
                                    <Heading text={"Setup AutoPay now"} desc={"With AutoPay, SIP investments are made automatically from your preferred bank."} />
                                    <APSizedBox height="16px" />
                                    <APText style={{ fontSize: "12px", lineHeight: 1.33, color: "#232730" }}>YOUR BANK ACCOUNT:</APText>
                                    <APSizedBox height="8px" />
                                    <BankAccount mandate={mandate} />
                                    {
                                        mandateType == "UpiMandate" &&
                                        <>
                                            <APFormFieldText
                                                label="UPI ID"
                                                onChanged={setUpiId}
                                                helperText={`Kindly use the UPI ID linked to your ${mandate.bankName?.split(" ")[0] ?? "selected bank"} account.`}
                                                validator={(v) => {
                                                    if (!v) {
                                                        return "Required";
                                                    }
                                                    if (!/^[0-9A-Za-z.-]{2,256}@[A-Za-z]{2,64}$/.test(v)) {
                                                        return "Invalid UPI Id";
                                                    }
                                                    return null;
                                                }}
                                            />
                                            <Heading
                                                text=""
                                                desc={"Please ensure that the bank account number and UPI ID (VPA) belong to the same account on your payment app."}
                                            />
                                        </>
                                    }
                                    <APSizedBox height="16px" />
                                    {
                                        (mandate.isAutoGenerated && mandateType === "Online") ?
                                            <AmountSelection
                                                mandateAmount={mandateAmount}
                                                onChange={(amount) => {
                                                    setMandateAmount(amount)
                                                }}
                                            />
                                            :
                                            <APRow mainAxisAlignment="spaceBetween">
                                                <APText style={{ fontSize: "12px", lineHeight: 1.33, color: "#41495b" }}> YOUR AUTOPAY LIMIT: </APText>
                                                <APText style={{ fontSize: "14px", lineHeight: 1.43, color: "#232730", fontWeight: 600 }}>{parseFloat(mandate.mandateAmount).currency()}</APText>
                                            </APRow>
                                    }
                                </APCard>
                                <APSizedBox height="8px" />
                                {
                                    mandateType === "Offline" &&
                                    <Note text={"Offline AutoPay setup usually takes 30-45 days to get approved."} />
                                }
                                {
                                    ["Online", "UpiMandate"].includes(mandateType) &&
                                    <>
                                        <Note text="Amount debited will be your SIP amount and not the max limit you have set." />
                                        <APSizedBox height="8px" />
                                        {
                                            mandateType == "UpiMandate" ? <UpiWhatNext upiId={upiId} bankName={mandate.bankName?.split(" ")[0] ?? mandate.getBankName()} />
                                                : <OnlineWhatsNext />
                                        }
                                    </>
                                }
                            </APColumn>
                        }

                        actions={
                            mandateType === "Offline" ?
                                <APAsyncButton
                                    onClick={async () => {
                                        try {
                                            let newMandateId = props.mandateId

                                            if (!newMandateId) {
                                                throw new Error("Mandate not found")
                                            }

                                            if (mandate.isAutoGenerated) {
                                                if (mandate.mandateAmount != mandateAmount) {
                                                    let res = await getIt(MutualFundApiServices).registerMandateExistingToNew({ mandateId: newMandateId, mandateAmount }, mandateToken)
                                                    newMandateId = res._id;
                                                }
                                            }

                                            window.location.href = `/mandate/${newMandateId}/instructions/offline`;
                                        } catch (error) {
                                            handleErrorMessage(error)
                                        }


                                    }}
                                >
                                    Continue
                                </APAsyncButton>
                                :
                                <APAsyncButton
                                    onClick={async () => {
                                        if (await control.validate()) {
                                            try {
                                                let newMandateId = props.mandateId

                                                if (!newMandateId) {
                                                    throw new Error("Mandate not found")
                                                }

                                                if (mandateType == "UpiMandate" && upiId) {
                                                    await getIt(MutualFundApiServices)
                                                        .createUpiMandate(mandateToken, { upiId, mandateId: newMandateId });
                                                    window.location.href = `/mandate/${newMandateId}/upiInstruction`;
                                                    return;
                                                }

                                                if (mandate.isAutoGenerated) {
                                                    if (mandate.mandateAmount != mandateAmount) {
                                                        let res = await getIt(MutualFundApiServices).registerMandateExistingToNew({ mandateId: newMandateId, mandateAmount }, mandateToken)
                                                        newMandateId = res._id;
                                                    }
                                                }

                                                window.location.href = `/mandate/${newMandateId}/paymentlink`;
                                            } catch (error) {
                                                handleErrorMessage(error)
                                            }
                                        }
                                    }}
                                >
                                    Setup
                                </APAsyncButton>
                        }
                    />
                </APForm>
            </APExpanded>
        </APColumn>

    );
}

function AmountSelection({ onChange, mandateAmount }: { onChange: (value: string) => void, mandateAmount: string }) {

    // If the specified amount is not present in LIMITS arrays
    if (!LIMITS.includes(mandateAmount)) {
        return (
            <APRow mainAxisAlignment="spaceBetween">
                <APText style={{ fontSize: "12px", lineHeight: 1.33, color: "#41495b" }}> YOUR AUTOPAY LIMIT: </APText>
                <APText style={{ fontSize: "14px", lineHeight: 1.43, color: "#232730", fontWeight: 600 }}>{parseFloat(mandateAmount).currency()}</APText>
            </APRow>
        )
    }

    return (
        <APColumn>
            <APText style={{ fontSize: "12px", lineHeight: 1.33, color: "#232730" }}>CHOOSE YOUR LIMIT:</APText>
            <APSizedBox height="8px" />
            <APGrid count={3} gapProps="8px">
                {
                    LIMITS.map((amount) => {
                        return (
                            <div
                                key={amount}
                                onClick={() => onChange(amount)}
                            >
                                <APCenter style={{
                                    borderRadius: "8px",
                                    border: mandateAmount == amount ? undefined : `solid 1px #e2e6ef`,
                                    color: mandateAmount == amount ? "white" : "#224d96",
                                    background: mandateAmount == amount ? "#224d96" : "#fff",
                                    cursor: 'pointer'
                                }}>
                                    <span style={{ lineHeight: 1.33, fontSize: "12px", fontWeight: 600, padding: "8px 0" }}>{parseInt(amount).currency()}</span>
                                </APCenter>
                            </div>
                        )
                    })
                }
            </APGrid>
            <APSizedBox height="8px" />
            <APText style={{ fontSize: "12px", lineHeight: 1.33, color: "#41495b" }}>A higher limit than your SIP is recommended to accommodate future investments.</APText>
        </APColumn>
    )
}

function OnlineWhatsNext() {
    return (
        <APCard style={{ borderRadius: "8px", padding: "16px" }}>
            <Heading
                text={"What’s next?"}
                desc={"After confirmation, you will be redirected to the netbanking page for AutoPay registration."}
            />
            <APSizedBox height="8px" />
            <Icons icon={"ic_confirmation_details"} text={"Confirm your details"} />
            <APSizedBox height="18px" />
            <Icons icon={"ic_login"} text={"Login via Netbanking/Debit Card"} />
            <APSizedBox height="18px" />
            <Icons icon={"ic_authenticate"} text={"Authenticate on the bank site"} />
        </APCard>
    )
}

function UpiWhatNext({ upiId, bankName }: { upiId?: string, bankName: string }) {
    const paymentApp = MandateModel.getUpiAppName(upiId, bankName);
    return <APCard style={{ borderRadius: "8px", padding: "16px" }}>
        <Heading
            text={"What’s next?"}
            desc={`After confirmation, please open your ${paymentApp} to complete the AutoPay registration.`}
        />
    </APCard>

}

export function RedirectOnline() {
    return (
        <APText
            style={{ color: "#41495b", fontSize: "12px", lineHeight: 1.33 }}
        >
            Changed your mind?
            <span style={{ color: "#5745ba", textDecoration: 'underline', fontSize: "12px", lineHeight: 1.33 }} onClick={async () => { }}>
                Click here
            </span> to setup via Netbanking
        </APText>
    )
}